/**
 * This file is part of the Valkyrie Robotics public website.
 * © 2025 Valkyrie Robotics, LLC. All rights reserved.
 */

import { motion } from "framer-motion";
import { Section } from "../components/Section";
import { SEO } from "../components/SEO";
import { FreeTrialSchedule } from "../components/FreeTrialSchedule";

export function V5Page() {
  return (
    <>
      <SEO
        title="VEX V5 Robotics Program"
        description="Advanced VEX V5 robotics coaching for ages 14+ in Forsyth County, GA. Master metal-based robotics, C++/Python programming, and compete at the highest levels. Perfect for high school and college students."
        canonical="/v5"
        keywords={[
          "VEX V5",
          "VEX V5 robotics",
          "high school robotics",
          "advanced robotics",
          "metal robotics",
          "C++ robotics",
          "Python robotics",
          "competitive robotics",
          "robotics engineering",
          "VEX V5 coaching",
        ]}
      />
      <div className="bg-neutral-900">
        {/* Hero Section */}
        <section className="relative overflow-hidden">
          <div className="mx-auto max-w-6xl px-4 py-24 text-gray-100">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <motion.h1
                  className="text-4xl md:text-5xl font-bold tracking-tight text-white"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  Competitive VEX V5 Robotics Coaching Program
                </motion.h1>
                <motion.p
                  className="text-xl text-brand-400 font-semibold"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                >
                  Advanced engineering, coding, and competition coaching for
                  middle and high school students (ages 12+)
                </motion.p>
                <motion.p
                  className="text-lg text-gray-300 leading-relaxed"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  If you're a parent exploring advanced STEM opportunities for
                  your middle or high schooler, you've likely encountered the
                  VEX V5 Robotics Competition (VRC). But what exactly does it
                  entail, and why is it a standout choice for students aspiring
                  to excel in engineering, programming, and leadership?
                </motion.p>
                <motion.div
                  className="flex flex-col sm:flex-row items-center gap-4"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                >
                  <motion.button
                    data-cal-namespace="free-trial"
                    data-cal-link="aplakosh/free-trial"
                    data-cal-config='{"layout":"month_view"}'
                    className="w-full sm:w-auto rounded-lg bg-brand-600 text-neutral-900 font-semibold px-8 py-4 text-lg transition-all duration-150 hover:scale-105 hover:bg-brand-500 active:scale-95 shadow-lg"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Book Free Trial Session
                  </motion.button>
                  <FreeTrialSchedule />
                </motion.div>
              </div>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="relative"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-brand-500/20 to-transparent rounded-2xl"></div>
                <img
                  src="/media/v5-competition.png"
                  alt="Students working with VEX V5 robotics equipment"
                  className="relative w-full h-auto object-cover rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* What Is VEX V5 Section */}
        <Section>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-gray-100">
                  What Is VEX V5?
                </h2>
                <p className="text-lg text-brand-400 font-medium">
                  Advanced Metal-Based Robotics Platform
                </p>
              </div>

              <div className="space-y-4 text-gray-300 text-lg leading-relaxed">
                <p>
                  VEX V5 is a metal-based robotics platform developed by VEX
                  Robotics, designed for high school and college students. It
                  offers a robust system for building sophisticated robots,
                  integrating powerful motors, sensors, and advanced programming
                  capabilities.
                </p>
                <p>
                  The platform emphasizes hands-on learning, critical thinking,
                  and collaboration, preparing students for real-world STEM
                  applications.
                </p>
              </div>
            </div>

            <div className="bg-neutral-800/30 rounded-2xl p-6 border border-neutral-700">
              <h3 className="text-xl font-semibold text-gray-100 mb-4">
                Key Features
              </h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start space-x-2">
                  <span className="text-brand-400 mt-1">•</span>
                  <span>Metal-based construction for durability</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-brand-400 mt-1">•</span>
                  <span>High-torque motors with encoders</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-brand-400 mt-1">•</span>
                  <span>Advanced sensor integration</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-brand-400 mt-1">•</span>
                  <span>Touchscreen controller interface</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-brand-400 mt-1">•</span>
                  <span>Professional-grade components</span>
                </li>
              </ul>
            </div>
          </div>
        </Section>

        {/* Building & Programming Section */}
        <Section>
          <div className="bg-neutral-800/30 rounded-2xl p-8 border border-neutral-700">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold tracking-tight mb-4 text-gray-100">
                How Do Students Build & Program Robots?
              </h2>
              <p className="text-lg text-gray-300 max-w-3xl mx-auto">
                Advanced construction and programming capabilities for
                sophisticated robotics
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-neutral-700/50 rounded-xl p-6 border border-neutral-600">
                <h3 className="text-xl font-semibold text-gray-100 mb-4">
                  Building System
                </h3>
                <p className="text-gray-300 mb-4">
                  The VEX V5 system utilizes durable metal components, allowing
                  students to construct complex and resilient robots. Each VEX
                  V5 kit includes professional-grade materials designed for
                  advanced applications.
                </p>
                <h4 className="font-medium text-brand-400 mb-2">
                  Each VEX V5 kit contains:
                </h4>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• Structural metal parts for frame and mechanisms</li>
                  <li>• Smart motors with integrated encoders</li>
                  <li>• Advanced sensors (vision, gyroscope, distance)</li>
                  <li>• V5 Robot Brain with touchscreen interface</li>
                  <li>• Professional controller for manual operation</li>
                  <li>• High-quality gears and mechanical components</li>
                </ul>
              </div>

              <div className="bg-neutral-700/50 rounded-xl p-6 border border-neutral-600">
                <h3 className="text-xl font-semibold text-gray-100 mb-4">
                  Programming
                </h3>
                <p className="text-gray-300 mb-4">
                  Programming is a core component of the VEX V5 experience.
                  Students use VEXcode V5, a versatile programming environment
                  that supports multiple skill levels and programming languages.
                </p>
                <h4 className="font-medium text-brand-400 mb-2">
                  Programming options:
                </h4>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• Block-based coding (drag-and-drop interface)</li>
                  <li>• Text-based coding in C++ and Python</li>
                  <li>• Advanced algorithmic thinking</li>
                  <li>• Sensor integration and automation</li>
                  <li>• Complex autonomous routines</li>
                  <li>• Industry-standard programming practices</li>
                </ul>
              </div>
            </div>
          </div>
        </Section>

        {/* Competitions Section */}
        <Section>
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-gray-100">
                  VEX V5 Competitions
                </h2>
                <p className="text-lg text-brand-400 font-medium">
                  Global Robotics Tournament Series
                </p>
              </div>

              <div className="space-y-4 text-gray-300 text-lg leading-relaxed">
                <p>
                  The VEX V5 Robotics Competition is a global tournament where
                  student teams design, build, and program robots to compete in
                  game-based engineering challenges. Each season introduces a
                  new game, requiring teams to strategize and adapt their robots
                  accordingly.
                </p>
                <p>
                  Competitions typically consist of Autonomous Period, where
                  robots operate based on pre-written code without human
                  intervention, and Driver-Controlled Period, where students
                  manually control their robots to complete tasks.
                </p>
                <p>
                  Teams advance through local, regional, and national
                  competitions, with the opportunity to qualify for the VEX
                  Robotics World Championship.
                </p>
              </div>
            </div>

            <div className="bg-neutral-800/30 rounded-2xl p-6 border border-neutral-700">
              <h3 className="text-xl font-semibold text-gray-100 mb-4">
                Competition Structure
              </h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-brand-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-brand-400 font-semibold text-sm">
                      1
                    </span>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-100">
                      Autonomous Period
                    </h4>
                    <p className="text-gray-300 text-sm">
                      Robots operate based on pre-written code without human
                      intervention.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-brand-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-brand-400 font-semibold text-sm">
                      2
                    </span>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-100">
                      Driver-Controlled Period
                    </h4>
                    <p className="text-gray-300 text-sm">
                      Students manually control their robots to complete tasks.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-brand-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-brand-400 font-semibold text-sm">
                      3
                    </span>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-100">
                      Advancement Path
                    </h4>
                    <p className="text-gray-300 text-sm">
                      Local → Regional → National → World Championship.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* Engineering Notebook Section */}
        <Section>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-gray-100">
                  Engineering Notebook
                </h2>
                <p className="text-lg text-brand-400 font-medium">
                  Critical Component for Success
                </p>
              </div>

              <div className="space-y-4 text-gray-300 text-lg leading-relaxed">
                <p>
                  The Engineering Notebook is a critical component of the VEX V5
                  program. It serves as a comprehensive record of the team's
                  design process, including concept sketches, design iterations,
                  and strategic planning.
                </p>
                <p>
                  Maintaining a detailed notebook is essential for eligibility
                  in judged awards such as the{" "}
                  <strong className="text-brand-400">Design Award</strong> and{" "}
                  <strong className="text-brand-400">Excellence Award</strong>,
                  which recognize outstanding engineering practices and team
                  performance.
                </p>
              </div>
            </div>

            <div className="bg-neutral-800/30 rounded-2xl p-6 border border-neutral-700">
              <h3 className="text-xl font-semibold text-gray-100 mb-4">
                Notebook Contents
              </h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start space-x-2">
                  <span className="text-brand-400 mt-1">•</span>
                  <span>Concept sketches and design iterations</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-brand-400 mt-1">•</span>
                  <span>Programming logic and testing results</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-brand-400 mt-1">•</span>
                  <span>Strategic planning and decision-making</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-brand-400 mt-1">•</span>
                  <span>Reflections on successes and improvements</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-brand-400 mt-1">•</span>
                  <span>Documentation for award consideration</span>
                </li>
              </ul>
            </div>
          </div>
        </Section>

        {/* Skills & Benefits Section */}
        <Section>
          <div className="bg-neutral-800/30 rounded-2xl p-8 border border-neutral-700">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold tracking-tight mb-4 text-gray-100">
                What Skills Will My Child Learn?
              </h2>
              <p className="text-lg text-gray-300 max-w-3xl mx-auto">
                Advanced competencies that prepare students for STEM careers and
                higher education
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-brand-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-brand-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-100 mb-2">
                  Engineering & Design
                </h3>
                <p className="text-gray-300 text-sm">
                  Developing solutions through iterative design and testing
                  processes
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-brand-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-brand-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-100 mb-2">
                  Programming Proficiency
                </h3>
                <p className="text-gray-300 text-sm">
                  Writing and debugging code in C++ or Python with industry
                  standards
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-brand-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-brand-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-100 mb-2">
                  Teamwork & Leadership
                </h3>
                <p className="text-gray-300 text-sm">
                  Collaborating effectively within a team environment and
                  leading projects
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-brand-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-brand-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-100 mb-2">
                  Strategic Planning
                </h3>
                <p className="text-gray-300 text-sm">
                  Analyzing game rules to develop winning strategies and tactics
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-brand-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-brand-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-100 mb-2">
                  Project Management
                </h3>
                <p className="text-gray-300 text-sm">
                  Managing time and resources to meet competition deadlines
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-brand-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-brand-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-100 mb-2">
                  Technical Documentation
                </h3>
                <p className="text-gray-300 text-sm">
                  Recording processes and decisions in comprehensive engineering
                  notebooks
                </p>
              </div>
            </div>
          </div>
        </Section>

        {/* Getting Started Section */}
        <Section>
          <div className="bg-gradient-to-r from-neutral-800/50 to-neutral-700/50 rounded-2xl p-12 border border-neutral-600">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold tracking-tight mb-4 text-gray-100">
                How Can My Child Join the VEX V5 Program?
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Multiple pathways to get involved in advanced competitive
                robotics
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-10">
              <div className="bg-neutral-700/30 rounded-xl p-6 border border-neutral-600">
                <div className="w-12 h-12 bg-brand-600/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-6 h-6 text-brand-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-100 mb-2 text-center">
                  School Teams
                </h3>
                <p className="text-gray-300 text-center text-sm">
                  Many high schools offer VEX V5 as part of their STEM
                  curriculum or extracurricular clubs.
                </p>
              </div>

              <div className="bg-neutral-700/30 rounded-xl p-6 border border-neutral-600">
                <div className="w-12 h-12 bg-brand-600/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-6 h-6 text-brand-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-100 mb-2 text-center">
                  Private Coaching
                </h3>
                <p className="text-gray-300 text-center text-sm">
                  Organizations like Valkyrie Robotics provide specialized
                  coaching and team opportunities.
                </p>
              </div>

              <div className="bg-neutral-700/30 rounded-xl p-6 border border-neutral-600">
                <div className="w-12 h-12 bg-brand-600/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-6 h-6 text-brand-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-100 mb-2 text-center">
                  Community Teams
                </h3>
                <p className="text-gray-300 text-center text-sm">
                  Local community centers or STEM organizations may host teams
                  open to students from various schools.
                </p>
              </div>
            </div>

            <div className="text-center">
              <motion.button
                data-cal-namespace="free-trial"
                data-cal-link="aplakosh/free-trial"
                data-cal-config='{"layout":"month_view"}'
                className="rounded-lg bg-brand-600 text-neutral-900 font-semibold px-10 py-4 text-xl transition-all duration-150 hover:scale-105 hover:bg-brand-500 active:scale-95 shadow-lg"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Book Free Trial Session
              </motion.button>
              <FreeTrialSchedule className="mt-4" />
              <p className="text-gray-400 mt-4">
                Questions about VEX V5?{" "}
                <a href="/contact" className="text-brand-400 hover:underline">
                  Contact us
                </a>{" "}
                or call (470) 239-0787
              </p>
            </div>
          </div>
        </Section>
      </div>
    </>
  );
}
